/**
 * Tooltip/Help System Schema
 *
 * Defines the structure for all tooltips in AICA.
 * Texts are stored in i18n JSON files (en.json, ru.json, uk.json).
 * This schema provides type safety and metadata for tooltip management.
 */

// Complexity levels determine tooltip behavior
export type TooltipComplexity = 'low' | 'medium' | 'high';

// User roles that can see the tooltip
// 'manager' is an alias for 'supervisor' in some contexts
export type TooltipRole = 'agent' | 'supervisor' | 'manager' | 'admin' | 'owner';

// Categories for organization
export type TooltipCategory =
  | 'settings.organization'
  | 'settings.project'
  | 'settings.ai'
  | 'settings.alerts'
  | 'settings.routing'
  | 'settings.crm'
  | 'settings.billing'
  | 'settings.notifications'
  | 'settings.team'
  | 'providers.asr'
  | 'providers.llm'
  | 'dashboard'
  | 'records'
  | 'scripts';

/**
 * Tooltip metadata stored in TypeScript registry.
 * Actual text content is in i18n JSON files.
 */
export interface TooltipMeta {
  /** Unique identifier matching i18n key path */
  id: string;

  /** Category for grouping */
  category: TooltipCategory;

  /** Complexity determines trigger behavior:
   * - low: hover on ? icon
   * - medium: hover + "Learn more" link
   * - high: click only, rich content with examples
   */
  complexity: TooltipComplexity;

  /** Roles that should see this tooltip (empty = all) */
  roles: TooltipRole[];

  /** Onboarding integration */
  onboarding?: {
    /** Tour step number (1-based) */
    step: number;
    /** Tour type */
    tour: 'admin' | 'agent' | 'supervisor';
    /** Highlight element during tour */
    highlight?: boolean;
  };

  /** Has rich content (comparison tables, code examples) */
  isRich?: boolean;

  /** Docs slug (e.g. 'user-guide/settings'), resolved to an absolute website
   * docs URL via getDocsUrl() at render time — not a raw href. */
  learnMorePath?: string;
}

/**
 * i18n tooltip content structure.
 * These fields should exist in en.json under tooltips.[id]
 */
export interface TooltipContent {
  /** Short title (optional for low complexity) */
  title?: string;

  /** Main body text (1-3 sentences) */
  body: string;

  /** Additional details for medium/high complexity */
  details?: string;

  /** Placeholder for examples (high complexity) */
  example?: string;
}

/**
 * Rich tooltip content for complex fields.
 * Used for provider comparisons, DSL syntax, etc.
 */
export interface RichTooltipContent extends TooltipContent {
  /** Comparison table data */
  comparison?: Array<{
    option: string;
    pros: string;
    cons: string;
  }>;

  /** Code/syntax examples */
  codeExamples?: Array<{
    label: string;
    code: string;
  }>;

  /** Warning message */
  warning?: string;
}

/**
 * Combined tooltip data (meta + content from i18n)
 */
export interface Tooltip {
  meta: TooltipMeta;
  content: TooltipContent | RichTooltipContent;
}

/**
 * Trigger behavior based on complexity
 */
export const TRIGGER_CONFIG: Record<
  TooltipComplexity,
  {
    trigger: 'hover' | 'click';
    showIcon: boolean;
    showLearnMore: boolean;
    delayMs: number;
  }
> = {
  low: {
    trigger: 'hover',
    showIcon: true,
    showLearnMore: false,
    delayMs: 200,
  },
  medium: {
    trigger: 'hover',
    showIcon: true,
    showLearnMore: true,
    delayMs: 300,
  },
  high: {
    trigger: 'click',
    showIcon: true,
    showLearnMore: true,
    delayMs: 0, // Instant on click
  },
};

/**
 * WCAG 2.1 compliant tooltip timing
 */
export const ACCESSIBILITY_CONFIG = {
  /** Minimum time before auto-dismiss (ms) */
  minDisplayTime: 500,

  /** Hover stays open while cursor inside */
  persistOnHover: true,

  /** Escape key dismisses tooltip */
  escapeKeyDismiss: true,

  /** Focus triggers tooltip (not just hover) */
  focusTrigger: true,

  /** Touch devices use click instead of hover */
  touchClickFallback: true,
};

// ============================================================
// Page-level help (NEW)
// ============================================================

// IDs for application pages and dashboard sub-tabs
export type PageId =
  | 'dashboard'
  | 'records'
  | 'trends'
  | 'agents'
  | 'alerts'
  // Dashboard sub-tab pages
  | 'conversion'
  | 'questions'
  | 'aiFeedback'
  | 'processing'
  | 'errors'
  | 'quality'
  | 'coaching'
  | 'agentPerformance'
  | 'agentProgress'
  | 'projects'
  | 'budget'
  // Role-specific overview tabs
  | 'ownerOverview'
  | 'adminOverview'
  | 'supervisorOverview'
  | 'clientOverview'
  | 'highlights'
  | 'criteriaReport'
  // «Голос клиента» (#848)
  | 'voiceOfCustomer';

/** Metadata for a page-level help entry */
export interface PageHelpEntry {
  type: 'page';
  /** Matches PageId */
  id: PageId;
  /** i18n key path, e.g. "pages.dashboard" inside the "help" namespace */
  i18nKey: string;
}

// ============================================================
// Metric tooltips (NEW)
// ============================================================

// Severity classification for metric threshold labels
export type ThresholdLevel = 'good' | 'warning' | 'danger';

/**
 * A threshold label with its associated severity level.
 * Used in MetricHelpEntry to render colour-coded range indicators.
 */
export interface MetricThreshold {
  label: string;
  level: ThresholdLevel;
}

/**
 * Provenance anchor — what a metric is grounded in, so the tooltip can disclose
 * whether a number is a real measurement or an AI judgement (WS7 gate F-3).
 *  - external:    real figure from systems/providers (counts, revenue, health)
 *  - human:       entered by a person (e.g. agent star ratings)
 *  - llm-opinion: an AI assessment from the transcript — a model's judgement,
 *                 not a verified fact or outcome
 *  - derived:     computed from other figures (trends, ranks, averages, ratios)
 */
export type MetricAnchor = 'external' | 'human' | 'llm-opinion' | 'derived';

export interface MetricHelpEntry {
  type: 'metric';
  /** Unique metric ID, e.g. "avgScore" */
  id: string;
  /** i18n key path, e.g. "metrics.avgScore" inside the "help" namespace */
  i18nKey: string;
  /** Provenance anchor surfaced as a badge in the tooltip (WS7 gate F-3). */
  anchor?: MetricAnchor;
}
